import React, { useEffect, useRef, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { adminApi } from '../api/client';

/**
 * Server-backed candidate picker. Scales to thousands of candidates by
 * deferring the search to the backend instead of preloading the full list.
 *
 * Props:
 *   value            — currently-selected candidate id (string) or '' / null.
 *   initialLabel     — optional preset display label (used when the parent
 *                      already has the candidate name, e.g. on edit). When
 *                      provided, we skip the round-trip to resolve the
 *                      initial value.
 *   onChange(id, candidate?) — fired when the user picks a result or clears.
 *                              `candidate` is the full minimal record
 *                              from the search results (or null on clear).
 *   placeholder      — input placeholder.
 *   disabled         — disable the entire control.
 *   testid           — base data-testid; falls back to "candidate-autocomplete".
 *
 * UX:
 *   - Type ≥ 1 character → debounced search (180ms).
 *   - Empty input + focused → show 10 most-recent candidates as suggestions.
 *   - Up/Down arrows + Enter to navigate keyboard-style.
 *   - Esc closes the dropdown without clearing.
 *   - Selected pill shows "Full name — Office (State)" with an × clear button.
 */
export default function CandidateAutocomplete({
  value,
  initialLabel = '',
  onChange,
  placeholder = 'Search candidates by name…',
  disabled = false,
  testid = 'candidate-autocomplete',
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  // `pickedLabel` is set the moment the user selects an option; while empty
  // we fall through to `initialLabel`, then to the async-resolved label.
  // Doing this derivation during render instead of via a sync-effect keeps
  // the lint rule "react-hooks/set-state-in-effect" happy.
  const [pickedLabel, setPickedLabel] = useState('');
  const [resolvedLabel, setResolvedLabel] = useState('');
  const [resolved, setResolved] = useState(!!initialLabel || !value);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selectedLabel = pickedLabel || initialLabel || resolvedLabel;

  // Resolve the initial value → label when parent doesn't supply one.
  useEffect(() => {
    if (resolved || !value) return;
    let active = true;
    (async () => {
      try {
        const res = await adminApi.get(`/admin/candidates/${value}`);
        if (active) {
          setResolvedLabel(formatCandidate(res.data));
        }
      } catch (_e) {
        if (active) setResolvedLabel('(unknown candidate)');
      } finally {
        if (active) setResolved(true);
      }
    })();
    return () => { active = false; };
  }, [value, resolved]);

  // Debounced search.
  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    let active = true;
    const handle = setTimeout(async () => {
      if (!active) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: '1', page_size: q ? '20' : '10' });
        if (q) params.set('q', q);
        params.set('sort', 'full_name');
        params.set('order', 'asc');
        const res = await adminApi.get(`/admin/candidates?${params.toString()}`);
        if (active) {
          setResults(res.data.items || []);
          setHighlight(0);
        }
      } catch (_e) {
        if (active) setResults([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 180);
    return () => { active = false; clearTimeout(handle); };
  }, [query, open]);

  // Close on click outside.
  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function pick(c) {
    setPickedLabel(formatCandidate(c));
    setResolved(true);
    setOpen(false);
    setQuery('');
    setResults([]);
    if (onChange) onChange(c.id, c);
  }

  function clear() {
    setPickedLabel('');
    setResolvedLabel('');
    setQuery('');
    setOpen(false);
    setResults([]);
    if (onChange) onChange('', null);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function onKeyDown(e) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      setHighlight((h) => Math.min(results.length - 1, h + 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setHighlight((h) => Math.max(0, h - 1));
      e.preventDefault();
    } else if (e.key === 'Enter') {
      if (results[highlight]) {
        pick(results[highlight]);
        e.preventDefault();
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const hasSelection = !!value && !!selectedLabel;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }} data-testid={testid}>
      {hasSelection ? (
        <div
          className="d-flex justify-content-between align-items-center px-2 py-2"
          style={{
            border: '1px solid var(--bs-border-color)',
            borderRadius: 6,
            background: 'var(--bs-tertiary-bg, #f8f9fa)',
            minHeight: 38,
          }}
          data-testid={`${testid}-selected`}
        >
          <span style={{ fontSize: '0.9rem' }}>{selectedLabel}</span>
          {!disabled && (
            <button
              type="button"
              className="btn btn-sm btn-link text-muted p-0 ms-2"
              onClick={clear}
              aria-label="Clear selection"
              data-testid={`${testid}-clear-button`}
              style={{ lineHeight: 1, textDecoration: 'none' }}
            >
              <i className="bi bi-x-circle" style={{ fontSize: '1.1rem' }} />
            </button>
          )}
        </div>
      ) : (
        <>
          <Form.Control
            ref={inputRef}
            type="text"
            value={query}
            placeholder={placeholder}
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onKeyDown={onKeyDown}
            autoComplete="off"
            spellCheck="false"
            data-testid={`${testid}-input`}
            aria-autocomplete="list"
            aria-expanded={open}
          />
          {open && (
            <div
              role="listbox"
              data-testid={`${testid}-dropdown`}
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: 4,
                background: 'var(--bs-body-bg, #fff)',
                border: '1px solid var(--bs-border-color)',
                borderRadius: 6,
                boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
                zIndex: 1080,
                maxHeight: 260,
                overflowY: 'auto',
              }}
            >
              {loading ? (
                <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
              ) : results.length === 0 ? (
                <div className="text-muted py-2 px-3" style={{ fontSize: '0.88rem' }}>
                  {query.trim() ? 'No candidates match.' : 'Start typing to search candidates.'}
                </div>
              ) : (
                results.map((c, idx) => (
                  <button
                    type="button"
                    key={c.id}
                    onMouseEnter={() => setHighlight(idx)}
                    onClick={() => pick(c)}
                    className="w-100 text-start px-3 py-2 border-0"
                    data-testid={`${testid}-option-${c.id}`}
                    style={{
                      background: highlight === idx ? 'var(--bs-primary-bg-subtle, #e7f1ff)' : 'transparent',
                      cursor: 'pointer',
                      borderBottom: idx < results.length - 1 ? '1px solid var(--bs-border-color-translucent, #e9ecef)' : 'none',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: '0.92rem' }}>{c.full_name}</div>
                    <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                      {[c.office, c.party, c.state && `(${c.state}${c.district ? `, ${c.district}` : ''})`].filter(Boolean).join(' · ')}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatCandidate(c) {
  if (!c) return '';
  const meta = [c.office, c.state && `(${c.state}${c.district ? `, ${c.district}` : ''})`].filter(Boolean).join(' · ');
  return meta ? `${c.full_name} — ${meta}` : c.full_name;
}
